<template>
  <transition name="dlg">
    <div v-if="open" class="dlg-overlay" @pointerdown="onOverlayPointerDown" @pointerup="onOverlayPointerUp">
      <div class="dlg-card" :style="{ maxWidth: maxWidth + 'px' }" role="dialog" aria-modal="true">
        <div v-if="title || showClose" class="dlg-card__header">
          <div class="dlg-card__title">{{ title }}</div>
          <button v-if="showClose" class="dlg-card__close" @click="$emit('close')" aria-label="Close">✕</button>
        </div>
        <div class="dlg-card__body"><slot /></div>
        <div v-if="$slots.actions" class="dlg-card__actions"><slot name="actions" /></div>
      </div>
    </div>
  </transition>
</template>

<script>
// Reusable centered dialog (backdrop + card). Not an Ionic modal.
// Usage:
//   <Dialog :open="show" title="Title" @close="show = false"> body </Dialog>
//   <Dialog :open="show" :dismissible="false"> ... <template #actions> buttons </template> </Dialog>
export default {
  name: 'app-dialog',
  props: {
    open: { type: Boolean, default: false },
    // When false, clicking the backdrop / pressing Esc will NOT close it.
    dismissible: { type: Boolean, default: true },
    // Shows an explicit ✕ button in the top-right that emits 'close'.
    showClose: { type: Boolean, default: false },
    title: { type: String, default: '' },
    maxWidth: { type: Number, default: 400 },
  },
  data: function () {
    // Tracks whether the pointer press started on the backdrop itself, so a
    // drag that begins inside the card (e.g. selecting text) and releases on the
    // backdrop does NOT count as a backdrop click.
    return { pressedOnBackdrop: false }
  },
  watch: {
    open: function (val) {
      if (val) document.addEventListener('keydown', this.onKeydown)
      else document.removeEventListener('keydown', this.onKeydown)
    },
  },
  mounted: function () {
    if (this.open) document.addEventListener('keydown', this.onKeydown)
  },
  beforeDestroy: function () {
    document.removeEventListener('keydown', this.onKeydown)
  },
  methods: {
    onOverlayPointerDown: function (e) {
      // Arm only when the press itself lands on the backdrop (not the card).
      this.pressedOnBackdrop = e.target === e.currentTarget
    },
    onOverlayPointerUp: function (e) {
      // Close only when BOTH the press and the release happened on the backdrop.
      var upOnBackdrop = e.target === e.currentTarget
      if (this.pressedOnBackdrop && upOnBackdrop && this.dismissible) {
        this.$emit('close')
      }
      this.pressedOnBackdrop = false
    },
    onKeydown: function (e) {
      if (e.key === 'Escape' && this.dismissible) this.$emit('close')
    },
  },
}
</script>

<style scoped>
.dlg-overlay {
  position: fixed;
  inset: 0;
  z-index: 30000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(0, 0, 0, 0.5);
  -webkit-backdrop-filter: blur(2px);
  backdrop-filter: blur(2px);
}
.dlg-card {
  width: 100%;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 16px;
  background: var(--ion-color-light);
  color: var(--ion-text-color);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.35);
}
.dlg-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 14px 12px 4px 18px;
}
.dlg-card__title {
  font-size: 18px;
  font-weight: 700;
}
.dlg-card__close {
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: var(--ion-text-color);
  font-size: 16px;
  line-height: 1;
  opacity: 0.6;
  cursor: pointer;
}
.dlg-card__close:hover {
  opacity: 1;
  background: rgba(128, 128, 128, 0.15);
}
.dlg-card__body {
  padding: 14px 18px;
  overflow-y: auto;
}
.dlg-card__actions {
  padding: 8px 18px 16px;
  display: flex;
  gap: 10px;
}

/* fade overlay + pop card */
.dlg-enter-active,
.dlg-leave-active {
  transition: opacity 0.2s ease;
}
.dlg-enter,
.dlg-leave-to {
  opacity: 0;
}
.dlg-enter-active .dlg-card,
.dlg-leave-active .dlg-card {
  transition: transform 0.2s ease;
}
.dlg-enter .dlg-card,
.dlg-leave-to .dlg-card {
  transform: scale(0.94);
}
@media (prefers-reduced-motion: reduce) {
  .dlg-enter-active .dlg-card,
  .dlg-leave-active .dlg-card {
    transition: none;
  }
}
</style>
